import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import type { DomainBehaviourDay, DomainBehaviourDayMetrics } from '@/domain/cages/overview.types';

import { daypartMetricSeries } from '../daypartSeries';

import type { DaypartChartSpecParams } from './types';

// ---------------------------------------------------------------------------
// Swim speed
// ---------------------------------------------------------------------------

export interface SwimSpeedChartSpecParams
    extends DaypartChartSpecParams<DomainBehaviourDayMetrics> {
    axisLabel: string;
}

const SWIM_SPEED_UNIT = 'bl/s';
const SWIM_SPEED_MIN = 0;
const SWIM_SPEED_MAX = 2.5;
const SWIM_SPEED_DECIMALS = 2;

/**
 * Swim speed in body lengths per second, one series per daypart.
 *
 * The axis is pinned to 0–2.5 rather than fitted: swim speed is read against
 * known thresholds, so a fitted axis would make a calm day look dramatic.
 *
 * Reads `fishSpeedAvg` from the licence-gated behaviour block returned by the
 * OV REST overview payload.
 */
export function buildSwimSpeedChartSpec(params: SwimSpeedChartSpecParams): MetricChartSpec<'line'> {
    const { days, graphedDaypartTags, activeDaypartTags, daypartLabel, locale, axisLabel } = params;

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: daypartMetricSeries({
            days,
            metric: 'fishSpeedAvg',
            graphedDaypartTags,
            activeDaypartTags,
            label: daypartLabel,
            keyPrefix: 'speed',
        }),
        left: {
            label: axisLabel,
            unit: SWIM_SPEED_UNIT,
            min: SWIM_SPEED_MIN,
            max: SWIM_SPEED_MAX,
            tickDecimals: 1,
            valueDecimals: SWIM_SPEED_DECIMALS,
        },
        locale,
    };
}

/** The most recent day with a swim-speed reading. Drives the info panel's date. */
export function findLastSwimSpeedDay(days: DomainBehaviourDay[]): DomainBehaviourDay | undefined {
    return days.findLast((day) => day.data.fishSpeedAvg !== null);
}
